import { useSyncExternalStore } from "react";
import { supabase } from "@/lib/supabase";

const KEY = "bb:clicked";
const MAX_LOCAL = 500;
const GO_HREF_RE = /\/api\/go\?(?:[^#]*&)?l=([0-9a-f-]{36})/i;
const EMPTY: ReadonlySet<string> = new Set();

function loadLocal(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = JSON.parse(window.localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

let local = new Set<string>(loadLocal());
let remote = new Set<string>();
let snapshot: ReadonlySet<string> = new Set([...local, ...remote]);
const subs = new Set<() => void>();

function emit() {
  snapshot = new Set([...local, ...remote]);
  subs.forEach((fn) => fn());
}

export function markClicked(id: string) {
  if (local.has(id)) return;
  local.add(id);
  if (local.size > MAX_LOCAL) local = new Set([...local].slice(-MAX_LOCAL));
  try {
    window.localStorage.setItem(KEY, JSON.stringify([...local]));
  } catch {
    // private mode / storage blocked: in-memory only
  }
  emit();
}

export async function syncClickedFromServer() {
  const { data, error } = await supabase.rpc("my_clicked_listing_ids");
  if (error || !Array.isArray(data)) return;
  remote = new Set(data as string[]);
  emit();
}

export function clearRemoteClicked() {
  if (remote.size === 0) return;
  remote = new Set();
  emit();
}

export function useClickedIds(): ReadonlySet<string> {
  return useSyncExternalStore(
    (fn) => {
      subs.add(fn);
      return () => subs.delete(fn);
    },
    () => snapshot,
    () => EMPTY,
  );
}

// Anchors to /api/go (cards, tables, drawers, static SEO pages) are marked here
// instead of per-component; window.open call sites call markClicked directly.
if (typeof document !== "undefined") {
  document.addEventListener(
    "click",
    (e) => {
      const a = (e.target as Element | null)?.closest?.("a[href]");
      const m = a && GO_HREF_RE.exec(a.getAttribute("href") ?? "");
      if (m) markClicked(m[1]);
    },
    true,
  );
}
